import { FormEvent, useCallback } from 'react'
import cx from 'classnames'
import { debounce } from 'lodash'

import Styles from './TaxonomyEdit.module.scss'
import { ExpandingTextArea } from 'common/ExpandingTextArea/ExpandingTextArea'
import { ListInput } from 'common/ListInput/ListInput'
import { ReactComponent as DownArrowIcon } from 'assets/icons/chevronDown24.svg'
import { ReactComponent as UpArrowIcon } from 'assets/icons/chevronUp24.svg'
import { ReactComponent as CloseIcon } from 'assets/icons/close24.svg'

export interface FormValues {
  howToUseThis: string[] | null
  whyTrustThis: string | null
}

interface Props {
  className?: string
  collapsed?: boolean
  handleChange: (event: FormEvent<HTMLTextAreaElement>) => void
  handleSubmit: (event?: FormEvent<HTMLFormElement>) => void
  onToggleCollapse?: () => void
  onToggleSuccessMessage?: () => void
  setFieldValue: (name: string, value: string[] | null) => void
  showSuccessMessage?: boolean
  values: FormValues
}

const DebounceTime = 1000
const HowToUseThisMaxLength = 250
const WhyTrustThisMaxLength = 125

export const TaxonomyEdit = ({
  className,
  collapsed,
  handleChange,
  handleSubmit,
  onToggleCollapse,
  onToggleSuccessMessage,
  setFieldValue,
  showSuccessMessage,
  values: { howToUseThis, whyTrustThis },
}: Props) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandleSubmit = useCallback(debounce(handleSubmit, DebounceTime), [handleSubmit])
  const onChange = (event?: FormEvent<HTMLFormElement>) => {
    if (event) {
      event.persist()
    }

    debouncedHandleSubmit(event)
  }

  return (
    <form className={cx(Styles.taxonomyEdit, className)} onChange={onChange}>
      {showSuccessMessage ? (
        <header className={Styles.formHeader}>
          <p className={cx(Styles.formHeading, Styles.header)}>
            Success! You've published your doc.
            <button className={Styles.closeButton} onClick={onToggleSuccessMessage} type="button">
              Finish later
              <CloseIcon />
            </button>
          </p>
          <p className={Styles.text}>
            Now that your doc is live, help your readers get the most out of your knowledge by answering these two
            questions:
          </p>
        </header>
      ) : (
        <header className={Styles.formHeader}>
          <button className={Styles.collapsedHeading} onClick={onToggleCollapse} type="button">
            <span aria-label="Waving hand" role="img">
              👋
            </span>{' '}
            How to use this doc
            {collapsed ? <DownArrowIcon className={Styles.arrowIcon} /> : <UpArrowIcon className={Styles.arrowIcon} />}
          </button>
        </header>
      )}
      {!collapsed && (
        <>
          <fieldset className={Styles.whyTrustThisFields}>
            <header className={Styles.whyTrustThisHeader}>
              <p className={Styles.fieldsetHeading}>Why trust this</p>
              <small className={Styles.characterCount}>{WhyTrustThisMaxLength - (whyTrustThis || '').length}</small>
            </header>

            <ExpandingTextArea
              autoComplete="off"
              maxLength={WhyTrustThisMaxLength}
              name="whyTrustThis"
              onChange={handleChange}
              placeholder="Let other users know why they should trust this"
              value={whyTrustThis || ''}
            />
            <p className={Styles.characterLimitNote}>{WhyTrustThisMaxLength} character limit</p>
          </fieldset>

          <fieldset>
            <header className={Styles.fieldsetHeading}>
              How to use this
              <small className={Styles.characterCount}>
                {HowToUseThisMaxLength - (howToUseThis || []).reduce((count, text) => count + text.length, 0)}
              </small>
            </header>

            <ListInput
              className={Styles.howToUseThisListInput}
              maxLength={HowToUseThisMaxLength}
              name="howToUseThis"
              onChange={howToUseThis => {
                setFieldValue('howToUseThis', howToUseThis)
                onChange()
              }}
              placeholder="Add some info on how to use this"
              value={howToUseThis}
            ></ListInput>
            <p className={Styles.characterLimitNote}>{HowToUseThisMaxLength} character limit</p>
          </fieldset>
        </>
      )}
    </form>
  )
}
